// S5 frontend — TanStack Query hooks for the v0.16.0 device registry.
// Single module, mirror of /api/v1/device-profiles, /tags, /device-groups,
// and /cameras/import + /cameras/test-connection.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface DeviceProfile {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  recording_enabled: boolean;
  recording_mode: "continuous" | "motion" | "schedule";
  retention_days: number;
  analytics_enabled: boolean;
  analytics_config: Record<string, unknown>;
  snapshot_interval_minutes: number;
  stream_quality: "main" | "sub";
  camera_count: number;
}

export interface DeviceProfileInput {
  name: string;
  description?: string | null;
  recording_enabled?: boolean;
  recording_mode?: "continuous" | "motion" | "schedule";
  retention_days?: number;
  analytics_enabled?: boolean;
  analytics_config?: Record<string, unknown>;
  snapshot_interval_minutes?: number;
  stream_quality?: "main" | "sub";
}

export interface DeviceGroup {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  camera_count: number;
}

export interface DeviceGroupDetail extends DeviceGroup {
  cameras: { id: string; name: string; status: string }[];
}

export interface TagRow {
  tag: string;
  camera_count: number;
}

export interface ImportResult {
  job_id: string;
  imported_count: number;
  failed_count: number;
  cameras: { id: string; name: string }[];
}

export interface ImportErrorRow {
  row: number;
  column: string;
  error: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  device_info: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------
const profilesKey = ["device-profiles"] as const;

export function useDeviceProfiles() {
  return useQuery({
    queryKey: profilesKey,
    queryFn: async () => {
      const { data } = await api.get<DeviceProfile[]>("/api/v1/device-profiles");
      return data;
    },
  });
}

export function useDeviceProfile(id: string | undefined) {
  return useQuery({
    queryKey: [...profilesKey, id],
    queryFn: async () => {
      const { data } = await api.get<DeviceProfile>(
        `/api/v1/device-profiles/${id}`,
      );
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateDeviceProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeviceProfileInput) => {
      const { data } = await api.post<DeviceProfile>(
        "/api/v1/device-profiles",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: profilesKey }),
  });
}

export function useUpdateDeviceProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<DeviceProfileInput>;
    }) => {
      const { data } = await api.put<DeviceProfile>(
        `/api/v1/device-profiles/${id}`,
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: profilesKey }),
  });
}

export function useDeleteDeviceProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/device-profiles/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: profilesKey }),
  });
}

export function useApplyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      cameraIds,
    }: {
      id: string;
      cameraIds: string[];
    }) => {
      const { data } = await api.post<{ updated_count: number }>(
        `/api/v1/device-profiles/${id}/apply`,
        { camera_ids: cameraIds },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profilesKey });
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------
const tagsKey = ["device-tags"] as const;

export function useDeviceTags() {
  return useQuery({
    queryKey: tagsKey,
    queryFn: async () => {
      const { data } = await api.get<TagRow[]>("/api/v1/tags");
      return data;
    },
  });
}

export function useAddTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cameraId,
      tag,
    }: {
      cameraId: string;
      tag: string;
    }) => {
      await api.post(`/api/v1/cameras/${cameraId}/tags`, { tag });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tagsKey });
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
  });
}

export function useRemoveTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cameraId,
      tag,
    }: {
      cameraId: string;
      tag: string;
    }) => {
      await api.delete(
        `/api/v1/cameras/${cameraId}/tags/${encodeURIComponent(tag)}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tagsKey });
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------
const groupsKey = ["device-groups"] as const;

export function useDeviceGroups() {
  return useQuery({
    queryKey: groupsKey,
    queryFn: async () => {
      const { data } = await api.get<DeviceGroup[]>("/api/v1/device-groups");
      return data;
    },
  });
}

export function useDeviceGroup(id: string | undefined) {
  return useQuery({
    queryKey: [...groupsKey, id],
    queryFn: async () => {
      const { data } = await api.get<DeviceGroupDetail>(
        `/api/v1/device-groups/${id}`,
      );
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const { data } = await api.post<DeviceGroup>(
        "/api/v1/device-groups",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: groupsKey }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/device-groups/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: groupsKey }),
  });
}

// ---------------------------------------------------------------------------
// Bulk import
// ---------------------------------------------------------------------------
export async function downloadImportTemplate(): Promise<Blob> {
  const r = await api.get("/api/v1/cameras/import/template", {
    responseType: "blob",
  });
  return r.data as Blob;
}

export function useImportCameras() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<ImportResult> => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<ImportResult>(
        "/api/v1/cameras/import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cameras"] }),
  });
}

// ---------------------------------------------------------------------------
// Test connection + validate stream
// ---------------------------------------------------------------------------
export interface TestConnectionInput {
  protocol: "rtsp" | "onvif" | "isapi";
  rtsp_url?: string;
  ip?: string;
  port?: number;
  username?: string;
  password?: string;
  channel?: number;
}

export function useTestConnection() {
  return useMutation({
    mutationFn: async (
      payload: TestConnectionInput,
    ): Promise<TestConnectionResponse> => {
      const { data } = await api.post<TestConnectionResponse>(
        "/api/v1/cameras/test-connection",
        payload,
      );
      return data;
    },
  });
}

export function useValidateStream() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cameraId: string) => {
      await api.get(`/api/v1/cameras/${cameraId}/validate-stream`);
    },
    onSuccess: (_d, cameraId) => {
      qc.invalidateQueries({ queryKey: ["cameras", "detail", cameraId] });
    },
  });
}
